import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MemberTerminationStatusClient, MemberTerminationStatusDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberTerminationStatusClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const useMemberTerminationStatuses = () => {
    return useQuery({
        queryKey: ['memberTerminationStatuses'],
        queryFn: async () => {
            const response = await apiClient.searchMemberTerminationStatuses({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

// Uncached search for admin tables
export const useMemberTerminationStatusSearch = () => {
    const search = async (params: any) => {
        const response = await apiClient.searchMemberTerminationStatuses(params);
        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount || 0,
        };
    };

    return { search };
};

// Query by ID
export const useMemberTerminationStatusById = (id: string) => {
    return useQuery({
        queryKey: ['memberTerminationStatus', id],
        queryFn: async () => {
            const response = await apiClient.getMemberTerminationStatusById(id);
            return response?.data || null;
        },
        enabled: !!id,
    });
};

// Create mutation
export const useCreateMemberTerminationStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: MemberTerminationStatusDTO) => {
            return await apiClient.createMemberTerminationStatus(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberTerminationStatuses'] });
        },
    });
};

// Update mutation
export const useUpdateMemberTerminationStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: MemberTerminationStatusDTO }) => {
            return await apiClient.updateMemberTerminationStatus(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberTerminationStatuses'] });
        },
    });
};

// Delete mutation
export const useDeleteMemberTerminationStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return await apiClient.deleteMemberTerminationStatus(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberTerminationStatuses'] });
        },
    });
};